import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ControlCenter, ControlOutcome } from "@/features/admin";
import { cn } from "@/lib/utils";
import {
  ArrowDownRightIcon,
  ArrowUpRightIcon,
  CalendarCheckIcon,
  CalendarDotsIcon,
  CalendarXIcon,
  CalendarIcon,
  InfoIcon,
  MinusCircleIcon,
  PhoneTransferIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { compact, compareLabel, percent } from "./format";

/**
 * Label, colour and icon per outcome, in the order they are read: what the agent got done,
 * from the most to the least concrete. Booking colours are the tenant dashboard's
 * (`features/sessions/classification.ts`), so "RDV pris" is the same blue on both sides.
 */
const OUTCOME_META: Record<
  ControlOutcome,
  { label: string; hint: string; color: string; icon: typeof CalendarIcon }
> = {
  booking_creation: {
    label: "RDV pris",
    hint: "Un rendez-vous a été créé",
    color: "#57BCFF",
    icon: CalendarCheckIcon,
  },
  booking_reschedule: {
    label: "RDV modifié",
    hint: "Un rendez-vous a été déplacé",
    color: "#9076F7",
    icon: CalendarDotsIcon,
  },
  booking_cancellation: {
    label: "RDV annulé",
    hint: "Un rendez-vous a été annulé",
    color: "#F13FAA",
    icon: CalendarXIcon,
  },
  transfer: {
    label: "Transféré",
    hint: "Transfert lancé vers un collègue, un autre agent ou l'équipe HeyUp",
    color: "#00BFA6",
    icon: PhoneTransferIcon,
  },
  booking: {
    label: "Agenda consulté",
    hint: "Créneaux regardés ou lien de réservation envoyé, sans RDV pris",
    color: "#C345E9",
    icon: CalendarIcon,
  },
  information: {
    label: "Renseignement",
    hint: "Réponse tirée de la base de connaissances",
    color: "#FFA415",
    icon: InfoIcon,
  },
  nothing: {
    label: "Rien d'abouti",
    hint: "Aucune action de l'agent n'a réussi",
    color: "#94A3B8",
    icon: MinusCircleIcon,
  },
};

/**
 * What the conversations *did*: one outcome each, the most concrete action that succeeded.
 * Calls by default — the question is usually "how many calls ended in a booking" — with
 * every conversation one click away.
 */
export function CallOutcomes({ data }: { data: ControlCenter }) {
  const [scope, setScope] = useState<"calls" | "sessions">("calls");
  const rows = data.outcomes.map((o) => ({
    ...o,
    value: o[scope],
    before:
      data.previous_outcomes.find((p) => p.key === o.key)?.[scope] ?? 0,
  }));
  const total = rows.reduce((sum, r) => sum + r.value, 0);
  const max = Math.max(1, ...rows.map((r) => r.value));
  const bookings = rows
    .filter((r) => r.key.startsWith("booking_"))
    .reduce((sum, r) => sum + r.value, 0);

  return (
    <Card className="h-full">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="text-sm">Ce que les appels ont donné</CardTitle>
          <p className="text-xs text-muted-foreground">
            {total
              ? `${compact(total)} ${scope === "calls" ? "appel(s)" : "conversation(s)"} terminé(e)s · ${percent(bookings, total)} avec une action sur un RDV`
              : "Conversations terminées sur la période"}
          </p>
        </div>
        <div className="flex rounded-md border p-0.5 text-xs">
          {(
            [
              ["calls", "Appels"],
              ["sessions", "Tout"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setScope(key)}
              className={cn(
                "rounded px-2 py-1 transition-colors",
                scope === key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {total ? (
          <>
            {/* The whole in one bar, then each outcome on its own line. */}
            <div className="mb-4 flex h-3 w-full overflow-hidden rounded-full bg-muted">
              {rows.map((r) =>
                r.value ? (
                  <div
                    key={r.key}
                    className="h-full transition-all duration-700"
                    style={{
                      width: `${(r.value / total) * 100}%`,
                      background: OUTCOME_META[r.key].color,
                    }}
                    title={`${OUTCOME_META[r.key].label} : ${r.value}`}
                  />
                ) : null,
              )}
            </div>
            <ul className="grid gap-x-8 gap-y-2.5 sm:grid-cols-2">
              {rows.map((r) => {
                const meta = OUTCOME_META[r.key];
                const Icon = meta.icon;
                const delta =
                  r.before > 0 ? ((r.value - r.before) / r.before) * 100 : null;
                return (
                  <li key={r.key} className="text-xs" title={meta.hint}>
                    <div className="mb-1 flex items-center gap-2">
                      <Icon
                        size={15}
                        weight="duotone"
                        style={{ color: meta.color }}
                        className="shrink-0"
                      />
                      <span className="truncate font-medium">{meta.label}</span>
                      <span className="ml-auto text-base font-semibold tabular-nums">
                        {compact(r.value)}
                      </span>
                      <span className="w-10 text-right tabular-nums text-muted-foreground">
                        {percent(r.value, total)}
                      </span>
                      <span className="w-12 text-right">
                        {delta !== null && Math.abs(delta) >= 1 ? (
                          <span
                            className="inline-flex items-center text-[11px] tabular-nums text-muted-foreground"
                            title={compareLabel(data.compare)}
                          >
                            {delta > 0 ? (
                              <ArrowUpRightIcon size={11} weight="bold" />
                            ) : (
                              <ArrowDownRightIcon size={11} weight="bold" />
                            )}
                            {Math.abs(delta).toFixed(0)} %
                          </span>
                        ) : null}
                      </span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${(r.value / max) * 100}%`,
                          background: meta.color,
                        }}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <div className="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
            Aucune conversation terminée sur la période.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
